'use client';

import { useState } from 'react';

import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';

import { ProjectResponseAudio } from '@/shared-survey';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ProjectResponseQuestionText } from './project-response-question-text';
import {
  getHighlightedRatingValue,
  isStarRatingValueSelected,
} from './project-response-rating-scale-select.helpers';
import {
  colorMap,
  getStarIconUrl,
} from './project-response-rating-scale-star-select.helpers';
import { ProjectResponseComponentProps } from './project-response-renderer.type';

interface RatingScaleStarProps {
  options: {
    label: string;
    value: string;
  }[];
  labels: [string, string];
  hasLowLabelError: boolean;
  hasHighLabelError: boolean;
  value: string;
  tentativeValue?: string;
  onClick: (value: string) => void;
  readonly?: boolean;
  hideLabels?: boolean;
  className?: string;
  assets?: ProjectResponseComponentProps['assets'];
  onLabelChange?: (label: 'LOW' | 'HIGH', value: string) => void;
  labelMaxLength?: number;
  text?: string;
  contentClassName?: string;
  audio?: ProjectResponseAudio;
  isAiVoiceEnabled?: boolean;
  errorMessage?: string;
  app?: 'manager' | 'survey';
}

// test-exception: className/JSX markup only, no extractable logic; RTL not installed (.agents/rules/testing.md)
export default function RatingScaleStarSelect({
  options,
  labels,
  hasLowLabelError,
  hasHighLabelError,
  value,
  tentativeValue,
  onClick,
  readonly,
  className,
  hideLabels,
  assets,
  onLabelChange,
  labelMaxLength,
  text,
  contentClassName,
  audio,
  isAiVoiceEnabled,
  errorMessage,
  app,
}: RatingScaleStarProps) {
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  const highlightedValue = getHighlightedRatingValue(value, tentativeValue);

  const handleMouseEnter = (value: string) => {
    if (!readonly) {
      setHoveredValue(value);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoveredValue(null);
    }
  };

  return (
    <div data-test='rating-scale-star-select' className='flex flex-col gap-2'>
      <div
        data-warning={hasLowLabelError || hasHighLabelError}
        className={cn(
          'min-h-20 flex-col items-center justify-start gap-3 rounded-lg bg-white p-3 placeholder:flex data-[warning=true]:border-orange-700',
          app &&
            'border-none bg-(--brand-assistant-message-bg) backdrop-blur-sm',
          className
        )}
      >
        {isAiVoiceEnabled && (
          <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
        )}

        {text && (
          <ProjectResponseQuestionText
            text={text}
            className='text-(--brand-text-on-assistant-message)'
          />
        )}

        {assets && assets?.length > 0 && assets[0]?.url && (
          <ProjectResponseAssetItem asset={assets[0]} />
        )}

        <div
          data-hide-labels={hideLabels}
          className={cn(
            'flex flex-col items-start justify-start gap-1 self-stretch data-[hide-labels=false]:min-h-14',
            contentClassName
          )}
        >
          <div className='flex flex-wrap items-center justify-center gap-2 self-stretch'>
            {options.map((v, index) => {
              const isHovered =
                hoveredValue !== null && +v.value <= +hoveredValue;
              const isSelected = isStarRatingValueSelected(
                v.value,
                highlightedValue
              );
              return (
                <div
                  key={`${v.value}-${index}`}
                  onMouseEnter={() => handleMouseEnter(v.value)}
                  onMouseLeave={handleMouseLeave}
                  className='w-[calc(20%-8px)]'
                  data-test='rating-scale-star-option'
                >
                  <button
                    onClick={() => onClick(v.value)}
                    aria-label={v.label || v.value}
                    data-small={options.length > 5}
                    data-hovered={isHovered}
                    data-selected={isSelected}
                    className={cn(
                      'm-auto flex h-10 min-h-10 w-full min-w-10 flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 p-2 active:from-rose-100 active:to-indigo-100 active:ring-2 active:ring-sky-300 sm:min-w-12',
                      !app &&
                        'data-[selected=false]:bg-linear-to-r data-[selected=false]:from-rose-50 data-[selected=false]:to-indigo-50',
                      !app &&
                        'data-[hovered=false]:hover:bg-linear-to-r data-[hovered=false]:hover:from-rose-100 data-[hovered=false]:hover:to-indigo-100',
                      app && 'border-(--brand-message-border) bg-white',
                      isHovered && [colorMap[v.value]?.background],
                      isSelected && [
                        colorMap[highlightedValue]?.border,
                        colorMap[highlightedValue]?.background,
                      ],
                      app &&
                        isSelected &&
                        'border-(--brand-primary) bg-(--brand-primary) text-(--brand-primary-contrast) shadow-[0px_4px_24px_0px_rgba(52,52,111,0.24)]'
                    )}
                    disabled={readonly}
                    data-test='rating-scale-star-button'
                  >
                    <div
                      data-hovered={isHovered}
                      data-selected={isSelected}
                      className={
                        'text-center font-sans text-gray-900 transition-all'
                      }
                    >
                      {isHovered || isSelected ? (
                        '⭐'
                      ) : (
                        /* oxlint-disable-next-line nextjs/no-img-element */
                        <img
                          src={getStarIconUrl()}
                          width={24}
                          height={24}
                          className='size-6 transition-all'
                          alt=''
                        />
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
          {!hideLabels && (
            <div className='flex flex-col gap-2 self-stretch'>
              <div className='flex items-start justify-between'>
                {labels.map((label, index) => {
                  const position = index === 0 ? 'low' : 'high';
                  return (
                    <div
                      key={position}
                      data-position={position}
                      className='group flex items-center gap-1 text-center [font-family:var(--brand-font)] text-xs text-(--brand-text-on-assistant-message) data-[position=high]:flex-row-reverse'
                    >
                      {!onLabelChange ? (
                        label
                      ) : (
                        <input
                          data-position={position}
                          placeholder={
                            position === 'low' ? 'very bad' : 'very good'
                          }
                          maxLength={labelMaxLength}
                          className='w-min min-w-min appearance-none ring-0 group-data-[position=high]:text-right group-data-[position=low]:text-left'
                          value={label}
                          onChange={(e) =>
                            onLabelChange(
                              index === 0 ? 'LOW' : 'HIGH',
                              e.target.value
                            )
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error message outside the container */}
      {(hasLowLabelError || hasHighLabelError) && (
        <div className='ml-4 flex items-center gap-2 text-xs'>
          <FontAwesomeIcon
            icon={faWarning}
            className='size-4 text-orange-500'
          />
          <span className='text-orange-700'>
            {errorMessage ??
              "Endpoint labels need to be updated. Use 'Improve question' to generate them automatically."}
          </span>
        </div>
      )}
    </div>
  );
}
